import React, { useContext, useEffect } from 'react';
import { AuthContext } from './context/AuthContext';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, shallowEqual } from 'react-redux';
import { isGovCloudRegion, isLegacyLoginRescueActive } from './utils/helpers';

import AccountSetup from './views/Accounts/account-setup';
import Registration from './views/Registration';
import Confirmation from './views/Confirmation';
import Login from './views/Login/login';
import MfaLogin from './views/Mfa/mfa-login';
import MfaReset from './views/Mfa/mfa-reset';
import ForgotPassword from './views/Login/forgot-password';
import ResetPassword from './views/Login/reset-password';
import getQueryParams from './utils/query-params';
import { getCommonType, getAccountDetails } from './store';
import AccountSelection from './views/Accounts/account-selection';
import ResendConfirmation from './views/Login/resend-confirmation';
import LegacyWSLoginRescue from './views/Login/legacy-wslogin-rescue';
import MigrationConfirmatiion from './views/Migration/migrate-account-confirmation';
import SupportUnauthenticatedModal from './views/Support/support-unauthenticated-modal';
import AlertProvider from './components/alert/alert-provider';
import { ReactQueryDevtools } from 'react-query/devtools';
import PublicLanding from './views/PublicLanding/public-landing';
import WSLogin from './views/Login/ws-login';
import Onboarding from './views/Onboarding/onboarding';
import { SupportPage } from './views/Support';
import { useStorageState } from './hooks/useStorageState';
import OverlayLoader from './components/loader/overlay-loader';

const WebAppAuth = ({ globalEventDistributor }) => {
  const {
    locale,
    loggedOut,
    routeName,
    c1AccountsList,
    wsLoggedIn,
    isUnauthLoading,
    showSupportModal,
  } = useSelector(
    (state) => ({
      locale: getCommonType(state, 'locale'),
      routeName: getCommonType(state, 'appSelection'),
      loggedOut: getCommonType(state, 'loggingOut'),
      c1AccountsList: getAccountDetails(state, 'c1AccountsList'),
      wsLoggedIn: getCommonType(state, 'wsLoggedIn'),
      isUnauthLoading: getCommonType(state, 'isUnauthLoading'),
      showSupportModal: getCommonType(state, 'showSupportModal'),
    }),
    shallowEqual
  );

  const showNewLoginPage = isLegacyLoginRescueActive();
  const {
    isAuthenticating,
    setIsAuthenticating,
    isSAMLAuthenticated,
    setIsSAMLAuthentication,
    setIsAuthenticated,
    isAuthenticated,
    getCurrentSession,
    setEventDistributor,
  } = useContext(AuthContext);

  const [isV1InactiveAccount] = useStorageState('localStorage', 'C1C_V1_INACTIVE_ACCOUNT', false);

  /** locale code block */
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  const navigateTo = (routeTo) => {
    if (routeTo) {
      return history.pushState({}, '', routeTo);
    }
  };

  // Locale change useEffect hook needs to be above other useEffect hooks in order for the language change to take effect in subsequent useEffect()
  useEffect(() => {
    if (locale && i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);

  useEffect(() => {
    if (globalEventDistributor) {
      setEventDistributor(globalEventDistributor);
    }
  }, []);

  useEffect(() => {
    if (routeName === 'create-account') {
      setIsAuthenticating(false);
    }
  }, [routeName]);

  /** locale code block ends */

  useEffect(() => {
    const {
      event = '',
      invitationID = '',
      token = '',
      email = '',
      authCode = '',
      redirectTo,
      accountID: queryAccountID,
      theme: queryColorMode,
    } = getQueryParams(window.location);

    if (event === 'SignUp' || event === 'ResendCode') {
      navigateTo(`/login${window.location.search}`);
    }

    if (event === 'WSForgotPassword') {
      navigateTo(`/legacy-login${window.location.search}`);
    }

    if (event === 'ForgotPassword') {
      navigateTo(`/reset-password${window.location.search}`);
    }

    if (invitationID !== '') {
      if (event === 'InvitationSignUp') {
        history.pushState({ invitationToken: token, email }, '', '/register');
      } else {
        globalEventDistributor.dispatch({
          type: 'URL_HAS_INVITATION_ID',
          urlHasInvitationID: true,
        });
        navigateTo(`/login${window.location.search}`);
      }
    }

    if (event === 'redirectToCloudOne') {
      localStorage.setItem('C1C_SIGN_IN_METHOD', JSON.stringify('ws'));
    }

    // Check SAML Status
    if (event === 'SAML' && authCode.length > 0) {
      if (!isSAMLAuthenticated) {
        setIsSAMLAuthentication({
          authToken: authCode,
          colorMode: queryColorMode,
          accountID: queryAccountID,
          redirectTo,
        });
      }
    } else {
      // Proceed with V2 Login
      if (loggedOut) {
        setIsAuthenticated(false);
      } else {
        getCurrentSession();
      }
    }
  }, [loggedOut, isSAMLAuthenticated, setIsAuthenticated]);

  useEffect(() => {
    const { referrer = '' } = getQueryParams(window.location);
    if (referrer !== '') {
      localStorage.setItem('C1C_CAMPAIGN_REFERRER', referrer);
    }
  }, []);

  useEffect(() => {
    const { event = '' } = getQueryParams(window.location);
    if (isAuthenticated && c1AccountsList && c1AccountsList.length > 0) {
      setIsAuthenticating(false);

      if (
        (event === 'AWSMarketplaceSubscription' ||
          event === 'TrendMicroSubscription' ||
          event === 'AzureMarketplaceSubscription' ||
          event === 'GithubTemplateSecurityInstallation',
        event === 'CloudOneGithubInstallation')
      ) {
        globalEventDistributor.dispatch({ type: 'NAVIGATE_APP', appName: 'account-selection' });
      }
    }
  }, [c1AccountsList, isAuthenticated, setIsAuthenticating]);

  const AuthenticatedRoute = ({ children, ...rest }) => {
    return (
      <Route
        exact
        {...rest}
        render={(props) =>
          isAuthenticated ? (
            children
          ) : (
            <Redirect to={{ pathname: '/', state: { from: props.location } }} />
          )
        }
      />
    );
  };

  return (
    <div className="WebAppAuth" style={{ height: '100%', position: 'relative' }}>
      {showSupportModal && (
        <SupportUnauthenticatedModal
          isOpen={showSupportModal}
          onClose={() => {
            globalEventDistributor.dispatch({
              type: 'TOGGLE_DISPLAY_SUPPORT_MODAL',
            });
          }}
        />
      )}
      {isAuthenticating ? (
        <OverlayLoader isLoading={isAuthenticating} />
      ) : (
        <Router>
          <Switch>
            <AuthenticatedRoute path={['/create-account']}>
              <AccountSetup />
              <ReactQueryDevtools initialIsOpen={false} />
            </AuthenticatedRoute>
            <AuthenticatedRoute
              exact
              path={['/onboarding']}
              component={() => (isGovCloudRegion ? <Redirect to="/" /> : <Onboarding />)}
            />
            <AuthenticatedRoute path={['/account-selection']}>
              <AccountSelection showSearchFilter={true} />
              <ReactQueryDevtools initialIsOpen={false} />
            </AuthenticatedRoute>
            {isGovCloudRegion && (
              <AuthenticatedRoute path={['/support']}>
                <AlertProvider key="/support">
                  <SupportPage />
                </AlertProvider>
                <ReactQueryDevtools initialIsOpen={false} />
              </AuthenticatedRoute>
            )}
            {/* Login Flow */}
            <Route
              exact
              path="/login"
              component={() =>
                isAuthenticated ? <Redirect to="/home" /> : <Login hideHeading={true} />
              }
            />
            <Route
              exact
              path="/mfa-login"
              component={() => (isAuthenticated ? <Redirect to="/home" /> : <MfaLogin />)}
            />
            <Route
              exact
              path="/mfa-reset"
              component={() => (isAuthenticated ? <Redirect to="/home" /> : <MfaReset />)}
            />
            <Route exact path="/resend-confirmation" component={() => <ResendConfirmation />} />
            <Route exact path="/forgot-password" component={() => <ForgotPassword />} />
            <Route
              exact
              path="/reset-password"
              component={() => (isAuthenticated ? <Redirect to="/home" /> : <ResetPassword />)}
            />
            {/* Registration Flow */}
            <Route
              exact
              path={['/trial', '/endpoint-trial']}
              component={() => (isGovCloudRegion ? <Redirect to="/" /> : <Registration />)}
            />
            <Route exact path="/register" component={() => <Registration />} />
            <Route exact path="/confirmation" component={() => <Confirmation />} />
            <Route
              exact
              path="/update-account-confirmation"
              component={() => <MigrationConfirmatiion />}
            />
            <Route
              exact
              path={['/legacy-login']}
              component={() =>
                isGovCloudRegion ? (
                  <Redirect to="/" />
                ) : isAuthenticated ? (
                  <Redirect to="/home" />
                ) : !showNewLoginPage ? (
                  <WSLogin />
                ) : (
                  <LegacyWSLoginRescue />
                )
              }
            />
            <Route
              exact
              path={['/']}
              component={() => {
                return isUnauthLoading ? (
                  // TODO: remove loader code once V1->V2 migration is done
                  <OverlayLoader isLoading={isUnauthLoading} />
                ) : isAuthenticated || wsLoggedIn ? (
                  <Redirect to="/home" />
                ) : isV1InactiveAccount ? (
                  <Redirect to="/legacy-login" />
                ) : (
                  <PublicLanding />
                );
              }}
            />
            <Route exact path="/signin" component={() => <Redirect to="/login" />} />
          </Switch>
        </Router>
      )}
    </div>
  );
};

export default WebAppAuth;
